import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { usePathname, useSearchParams } from '@/lib/navigation';
import { getPersistedAuthUser, useAuthStore } from '@/lib/auth-context';
import { useEffect, useState } from 'react';

const OFFLINE_FOCUS_STORAGE_KEY = 'zhcb-offline-focus';

export function isOfflineWorkspacePath(pathname: string) {
  return pathname === '/me/offline' || /^\/audit-tasks\/\d+(?:\/form\/\d+)?\/?$/.test(pathname);
}

function isAuditTaskDetailPath(pathname: string) {
  return /^\/audit-tasks\/\d+\/?$/.test(pathname);
}

function isAuditTaskFormPath(pathname: string) {
  return /^\/audit-tasks\/\d+\/form\/\d+\/?$/.test(pathname);
}

export function getOfflineFocusIntent() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(OFFLINE_FOCUS_STORAGE_KEY) === '1';
}

export function setOfflineFocusIntent(enabled: boolean) {
  if (typeof window === 'undefined') return;
  if (enabled) {
    window.localStorage.setItem(OFFLINE_FOCUS_STORAGE_KEY, '1');
  } else {
    window.localStorage.removeItem(OFFLINE_FOCUS_STORAGE_KEY);
  }
}

export function useOfflineFocusMode() {
  const storeRole = useAuthStore((s) => s.user?.role);
  const role = storeRole ?? getPersistedAuthUser()?.role;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isOnline } = useNetworkStatus();
  const [focusIntent, setFocusIntent] = useState(getOfflineFocusIntent);

  const isAuditorOffline = role === 'auditor' && !isOnline;
  const isFieldEntry = searchParams.get('phase') === 'field';
  const canStayInOfflineWorkspace =
    pathname === '/me/offline' ||
    isAuditTaskFormPath(pathname) ||
    (isAuditTaskDetailPath(pathname) && (isFieldEntry || focusIntent));

  useEffect(() => {
    if (role === 'auditor' && isFieldEntry && canStayInOfflineWorkspace) {
      setOfflineFocusIntent(true);
      setFocusIntent(true);
      return;
    }

    if (isAuditorOffline) {
      if (canStayInOfflineWorkspace) {
        setOfflineFocusIntent(true);
        setFocusIntent(true);
      }
      return;
    }

    if (isOnline && !canStayInOfflineWorkspace) {
      setOfflineFocusIntent(false);
      setFocusIntent(false);
    }
  }, [canStayInOfflineWorkspace, isAuditorOffline, isFieldEntry, isOnline, role]);

  return {
    isOnline,
    role,
    isAuditorOffline,
    pathname,
    canStayInOfflineWorkspace,
    focusMode: role === 'auditor' && canStayInOfflineWorkspace && (!isOnline || focusIntent),
  };
}
